import { Flag } from "@appchoose/flag";
import { toZonedTime } from "date-fns-tz";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { BackIcon } from "~/components/ui/icons/back";
import type { Sale } from "~/graphql/graphql.gen";
import { StoreRegion } from "~/graphql/graphql.gen";
import { brandState, serverTimeState } from "~/stores/brand";
import { activeSaleIdState, isCurrent, isFuture } from "~/stores/sales";
import { formatDate } from "~/utils/date";

type SalesSelectorItemProps = {
  sale: Sale;
  showFlag: boolean;
};

export const SalesSelectorItem: React.FC<SalesSelectorItemProps> = ({
  sale,
  showFlag,
}: SalesSelectorItemProps) => {
  const brand = useAtomValue(brandState);
  const { i18n, t } = useTranslation();

  const serverTime = useAtomValue(serverTimeState);
  const activeSaleId = useAtomValue(activeSaleIdState);

  return (
    <>
      <div className="flex flex-1 flex-col justify-center gap-1">
        <p className="flex items-center gap-2">
          <span className="inline-block text-base font-semibold text-gray-900 first-letter:uppercase">
            {sale.selection_name ??
              (sale.start_at
                ? formatDate(
                    brand?.timezone
                      ? toZonedTime(new Date(sale.start_at), brand.timezone)
                      : new Date(sale.start_at),
                    i18n.language.replace("_", "-"),
                    {
                      month: "long",
                      year: "numeric",
                    },
                  )
                : null)}
          </span>

          {showFlag ? (
            <Flag
              flag={sale.store === StoreRegion.Us ? "US" : "EU"}
              viewBox="0 0 24 24"
              className="w-[18px]"
            />
          ) : null}
          {isFuture(sale, serverTime) ? (
            <span className="text-xs font-semibold text-green-900">
              {t("sale.next")}
            </span>
          ) : null}
          {isCurrent(sale, serverTime) ? (
            <span className="text-xs font-semibold text-green-900">
              {t("sale.current")}
            </span>
          ) : null}
        </p>
        <p className="flex items-center gap-2 text-sm text-gray-700">
          {sale.start_at
            ? formatDate(
                brand?.timezone
                  ? toZonedTime(new Date(sale.start_at), brand.timezone)
                  : new Date(sale.start_at),
                i18n.language.replace("_", "-"),
                {
                  day: "numeric",
                  month: "numeric",
                  year: "2-digit",
                },
              )
            : "..."}
          <BackIcon className="size-4 rotate-180" />
          {sale.end_at
            ? formatDate(
                brand?.timezone
                  ? toZonedTime(new Date(sale.end_at), brand.timezone)
                  : new Date(sale.end_at),
                i18n.language.replace("_", "-"),
                {
                  day: "numeric",
                  month: "numeric",
                  year: "2-digit",
                },
              )
            : "..."}
        </p>
      </div>
      {sale.id === activeSaleId ? (
        <div className="peer flex aspect-square size-[18px] items-center justify-center rounded-full border border-gray-700 text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-900/30 data-[state=checked]:text-green-900">
          <span className="flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3 fill-current text-current transition duration-300"
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
        </div>
      ) : null}
    </>
  );
};
